import React from "react";
import {Redirect} from "react-router-dom";
import {getAccountPath, getDashboardPath} from "../helpers/Util";

// lazy load route components, code split for chunking
// Authentication related pages
const Login = React.lazy(() => import(/* webpackChunkName: "login" */ "../pages/Authentication/Login"));
const Logout = React.lazy(() => import(/* webpackChunkName: "logout" */ "../pages/Authentication/Logout"));
const Register = React.lazy(() => import(/* webpackChunkName: "register" */ "../pages/Authentication/Register"));
const ForgetPwd = React.lazy(() => import(/* webpackChunkName: "forget-pwd" */ "../pages/Authentication/ForgetPassword"));
const UserProfile = React.lazy(() => import(/* webpackChunkName: "user-profile" */ "../pages/Profile/UserProfile"));
const KYC = React.lazy(() => import(/* webpackChunkName: "kyc" */ "../pages/Authentication/KYC"));

// Dashboard
const Dashboard = React.lazy(() => import(/* webpackChunkName: "dashboard" */ "../pages/Dashboard"));
const TokenDashboard = React.lazy(() => import(/* webpackChunkName: "token-dashboard" */ "../pages/TokenManagement/TokenDashboard"));
const Agents = React.lazy(() => import(/* webpackChunkName: "agents" */ "../pages/Agents"));
const Clients = React.lazy(() => import(/* webpackChunkName: "clients" */ "../pages/Clients"));
const Transactions = React.lazy(() => import(/* webpackChunkName: "transactions" */ "../pages/Transactions"));
const Products = React.lazy(() => import(/* webpackChunkName: "products" */ "../pages/Products"));
const Orders = React.lazy(() => import(/* webpackChunkName: "orders" */ "../pages/Orders"));
const Chart = React.lazy(() => import(/* webpackChunkName: "chart" */ "../pages/Chart"));
const Bds = React.lazy(() => import(/* webpackChunkName: "bds" */ "../pages/Bds"));
const Rias = React.lazy(() => import(/* webpackChunkName: "rias" */ "../pages/Rias"));
const Transfer = React.lazy(() => import(/* webpackChunkName: "transfer" */ "../pages/Transfer"));
const Report = React.lazy(() => import(/* webpackChunkName: "report" */ "../pages/Report/Report"));
const Invoice = React.lazy(() => import(/* webpackChunkName: "invoice" */ "../pages/Report/Invoice"));
const Rebalance = React.lazy(() => import(/* webpackChunkName: "rebalance" */ "../pages/Rebalance"));
const Approval = React.lazy(() => import(/* webpackChunkName: "approval" */ "../pages/Approval"));
const Otp = React.lazy(() => import(/* webpackChunkName: "otp" */ "../pages/Otp"));
const Connect = React.lazy(() => import(/* webpackChunkName: "connect" */ "../pages/Authentication/Connect"));
const Invite = React.lazy(() => import(/* webpackChunkName: "invite" */ "../pages/Authentication/Invite"));
const Staking = React.lazy(() => import(/* webpackChunkName: "staking" */ "../pages/TokenManagement/Staking"));
const TokenGrant = React.lazy(() => import(/* webpackChunkName: "token-grant" */ "../pages/TokenManagement/TokenGrant"));
const Balances = React.lazy(() => import(/* webpackChunkName: "balances" */ "../pages/Balances"));
const Governance = React.lazy(() => import(/* webpackChunkName: "governance" */ "../pages/TokenManagement/Governance"));
const TokenGrantTax = React.lazy(() => import(/* webpackChunkName: "token-tax-grant" */ "../pages/TokenManagement/TokenGrantTax"));
const UnimportedTransactions = React.lazy(() => import(/* webpackChunkName: "token-tax-grant" */ "../pages/Admin/UnimportedTransactions"));
const InProcessClients = React.lazy(() => import(/* webpackChunkName: "token-tax-grant" */ "../pages/Admin/InProcessClients"));
const Billing = React.lazy(() => import(/* webpackChunkName: "token-tax-grant" */ "../pages/Admin/Billing"));
const Users = React.lazy(() => import(/* webpackChunkName: "token-tax-grant" */ "../pages/Admin/Users"));
const CustomEnvironments = React.lazy(() => import(/* webpackChunkName: "custom-environments" */ "../pages/Admin/CustomEnvironments"));

// Terms pages
const PrivacyPolicy = React.lazy(() => import(/* webpackChunkName: "privacy-policy" */ "../pages/Terms/PrivacyPolicy"));
const TermsOfService = React.lazy(() => import(/* webpackChunkName: "terms-of-service" */ "../pages/Terms/TermsOfService"));
const TermsOfUse = React.lazy(() => import(/* webpackChunkName: "terms-of-use" */ "../pages/Terms/TermsOfUse"));

export const generateRoutes = () => {
    const authProtectedRoutes = [
        {path: "/dashboard", component: Dashboard},
        {path: "/token-dashboard", component: TokenDashboard},
        {path: "/staking", component: Staking},
        {path: "/governance", component: Governance},
        {path: "/token-grants", component: TokenGrant},
        {path: "/tax", component: TokenGrantTax},
        {path: `/${getAccountPath("bd")}`, component: Bds},
        {path: `/${getAccountPath("ria")}`, component: Rias},
        {path: `/${getAccountPath("agent")}`, component: Agents},
        {path: `/${getAccountPath("client")}`, component: Clients},
        {path: "/trade", component: Transactions},
        {path: "/products", component: Products},
        {path: "/profile", component: UserProfile},
        {path: "/orders", component: Orders},
        {path: "/chart", component: Chart},
        {path: "/unimported-transactions", component: UnimportedTransactions},
        {path: "/in-process-clients", component: InProcessClients},
        {path: "/billing", component: Billing},
        {path: "/users", component: Users},
        {path: "/custom-environments", component: CustomEnvironments},
        {path: "/balances", component: Balances},
        {path: "/transfer", component: Transfer},
        {path: "/rebalance", component: Rebalance},
        {path: "/bitgo-otp", component: Otp},
        {path: "/:org/bitgo-otp", component: Otp},

        // this route should be at the end of all other routes
        {
            path: "/",
            exact: true,
            component: () => <Redirect to={getDashboardPath()}/>,
        }
    ];

    const publicRoutes = [
        {path: "/logout", component: Logout},
        {path: "/login", component: Login},
        {path: "/:org/login", component: Login},
        {path: "/forgot-password", component: ForgetPwd},
        {path: "/register", component: Register},
        {path: "/:org/register", component: Register},
        {path: "/invite", component: Invite},
        {path: "/:org/invite", component: Invite},
        {path: "/report", component: Report},
        {path: "/:org/report", component: Report},
        {path: "/invoice", component: Invoice},
        {path: "/:org/invoice", component: Invoice},
        {path: "/:org/withdrawal-request", component: Approval},
        {path: "/withdrawal-request", component: Approval},
        {path: "/connect", component: Connect},
        {path: "/bitgo-trust-kyc", component: KYC},
        {path: "/privacy-policy", component: PrivacyPolicy},
        {path: "/terms-of-service-wm", component: TermsOfService},
        {path: "/terms-of-use-tkm", component: TermsOfUse}
    ];

    return {authProtectedRoutes, publicRoutes};
};
